/**
 * Base Upload Dialog Component
 * Reusable dialog structure for all document uploads
 */

import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DropZone } from '../upload/drop-zone';
import { FilePreviewWithOptions } from '../upload/file-preview-with-options';
import type { UploadFile } from '@/hooks/use-file-upload';

export interface UploadDialogContent {
  title: string;
  description: string;
  alert: {
    icon: ReactNode;
    message: ReactNode;
  };
  dropZone: {
    icon: ReactNode;
    title: string;
    subtitle: string;
    acceptedFormats: string;
  };
  buttons: {
    upload: string;
    browse: string;
    /**
     * Label for the "also run extraction" action (VALE-757). Present only on
     * dialogs whose document type is worth reading — the core estate documents.
     * Its absence is what makes a dialog filing-only.
     */
    uploadAndExtract?: string;
  };
}

export interface BaseUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: UploadDialogContent;

  // Upload state
  files: UploadFile[];
  hasFiles: boolean;
  isUploading: boolean;
  canUpload: boolean;

  // Operations
  onUpload: (extract: boolean) => Promise<void>;
  onClose: () => void;
  onFilesSelected: (files: File[]) => void;
  onRemoveFile: (fileId: string) => void;
  onFileTypeChange?: (fileId: string, fileType: string) => void;
  onCertifiedCopyChange?: (fileId: string, isCertified: boolean) => void;
  openFileDialog: () => void;
  getInputProps: () => InputHTMLAttributes<HTMLInputElement> & {
    ref?: Ref<HTMLInputElement>;
  };

  // Configuration
  acceptedFileTypes?: string[];
  hideFileTypeSelector?: boolean;
  onlyCrucial?: boolean;
  excludeCrucial?: boolean;
}

export function BaseUploadDialog({
  open,
  onOpenChange,
  content,
  files,
  hasFiles,
  isUploading,
  canUpload,
  onUpload,
  onClose,
  onFilesSelected,
  onRemoveFile,
  onFileTypeChange,
  onCertifiedCopyChange,
  openFileDialog,
  getInputProps,
  acceptedFileTypes = ['pdf', 'jpg', 'jpeg', 'png'],
  hideFileTypeSelector = true,
  onlyCrucial,
  excludeCrucial
}: BaseUploadDialogProps) {
  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      onOpenChange(open);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{content.title}</DialogTitle>
          <DialogDescription>{content.description}</DialogDescription>
        </DialogHeader>

        <Alert>
          {content.alert.icon}
          <AlertDescription>{content.alert.message}</AlertDescription>
        </Alert>

        <div className="min-w-0 space-y-4">
          {!hasFiles && (
            <DropZone
              onDrop={onFilesSelected}
              onClick={openFileDialog}
              disabled={isUploading}
              acceptedFileTypes={acceptedFileTypes}
            >
              {content.dropZone.icon}
              <p className="mb-2 text-sm font-medium">
                {content.dropZone.title}
              </p>
              <p className="text-muted-foreground text-xs">
                {content.dropZone.subtitle}
              </p>
              <p className="text-muted-foreground mt-2 text-xs">
                {content.dropZone.acceptedFormats}
              </p>
            </DropZone>
          )}

          {hasFiles && (
            <ScrollArea className="h-[300px] pr-4">
              <div className="space-y-3">
                {files.map((file) => (
                  <FilePreviewWithOptions
                    key={file.id}
                    file={file}
                    onRemove={onRemoveFile}
                    onFileTypeChange={onFileTypeChange || (() => {})}
                    onCertifiedCopyChange={onCertifiedCopyChange || (() => {})}
                    disabled={isUploading}
                    hideFileTypeSelector={hideFileTypeSelector}
                    onlyCrucial={onlyCrucial}
                    excludeCrucial={excludeCrucial}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {hasFiles && !!content.buttons.uploadAndExtract && (
          <p className="text-muted-foreground text-xs">
            Extracting reads this document and proposes contacts, assets and
            beneficiaries for you to review. You can also run it later from the
            Documents page.
          </p>
        )}

        <DialogFooter>
          <Button
            size="xs"
            density="compact"
            variant="outline"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          {!hasFiles && (
            <Button size="xs" density="compact" onClick={openFileDialog}>
              {content.buttons.browse}
            </Button>
          )}
          {hasFiles && (
            <Button
              size="xs"
              density="compact"
              onClick={() => onUpload(false)}
              disabled={!canUpload}
              variant={content.buttons.uploadAndExtract ? 'outline' : 'default'}
            >
              {isUploading ? 'Uploading...' : content.buttons.upload}
            </Button>
          )}
          {hasFiles && !!content.buttons.uploadAndExtract && (
            <Button
              size="xs"
              density="compact"
              onClick={() => onUpload(true)}
              disabled={!canUpload}
            >
              {isUploading ? 'Uploading...' : content.buttons.uploadAndExtract}
            </Button>
          )}
        </DialogFooter>

        <input {...getInputProps()} />
      </DialogContent>
    </Dialog>
  );
}
